import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RgbColor } from '../color-picker.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CdkDragMove } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-color-area',
  templateUrl: './color-area.component.html',
  styleUrls: ['./color-area.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ColorAreaComponent implements OnInit, OnDestroy, AfterViewInit {
  private _rgbColor: RgbColor;
  private changeSubject: Subject<RgbColor>;
  private changeSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  private wrapperDragEl: any;
  private indicatorEl: any;

  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('indicator', { static: true }) indicator: ElementRef;
  @ViewChild('wrapperDrag') wrapperDrag: ElementRef;

  private ctx: CanvasRenderingContext2D;

  @Input() set rgbColor(rgbColor: RgbColor) {
    if (JSON.stringify(this._rgbColor) !== JSON.stringify(rgbColor)) {
      this._rgbColor = rgbColor;
      this.drawArea();
    }
  }
  get rgbColor() {
    return this._rgbColor;
  }

  public width = 265;
  public height = 135;

  @Output() colorUpdated: EventEmitter<RgbColor> = new EventEmitter(true);

  constructor() {
    this.changeSubject = new Subject<RgbColor>();
  }

  private drawArea() {
    if (!this.ctx || !this._rgbColor) {
      return;
    }
    const [h, s, v] = this.rgbToHsv(this._rgbColor.r, this._rgbColor.g, this._rgbColor.b);
    this.fillAreaGradient(h);
    const x = s * this.width;
    const y = (1 - v) * this.height;
    this.setIndicatorPosition(x, y, true);
  }

  private rgbToHsv(r: number, g: number, b: number): [number, number, number] {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h: number,
      s: number,
      v = max;

    const d = max - min;
    s = max === 0 ? 0 : d / max;

    if (max === min) {
      h = 0; // Achromatic
    } else {
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h, s, v];
  }

  private hsvToRgb(h: number, s: number, v: number): [number, number, number] {
    let r: number, g: number, b: number;

    let i = Math.floor(h * 6);
    let f = h * 6 - i;
    let p = v * (1 - s);
    let q = v * (1 - f * s);
    let t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0:
        r = v;
        g = t;
        b = p;
        break;
      case 1:
        r = q;
        g = v;
        b = p;
        break;
      case 2:
        r = p;
        g = v;
        b = t;
        break;
      case 3:
        r = p;
        g = q;
        b = v;
        break;
      case 4:
        r = t;
        g = p;
        b = v;
        break;
      case 5:
        r = v;
        g = p;
        b = q;
        break;
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  private fillAreaGradient(hue: number) {
    const [r, g, b] = this.hsvToRgb(hue, 1, 1);
    this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;
    this.ctx.fillRect(0, 0, this.width, this.height);

    const grdWhite = this.ctx.createLinearGradient(0, 0, this.width, 0);
    grdWhite.addColorStop(0, 'rgba(255,255,255,1)');
    grdWhite.addColorStop(1, 'rgba(255,255,255,0)');
    this.ctx.fillStyle = grdWhite;
    this.ctx.fillRect(0, 0, this.width, this.height);

    const grdBlack = this.ctx.createLinearGradient(0, 0, 0, this.height);
    grdBlack.addColorStop(0, 'rgba(0,0,0,0)');
    grdBlack.addColorStop(1, 'rgba(0,0,0,1)');
    this.ctx.fillStyle = grdBlack;
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  public dragMoved(e: CdkDragMove) {
    const fromLeftOfDraggedElement = e.pointerPosition.x - this.indicatorEl.getBoundingClientRect().left;
    const fromTopOfDraggedElement = e.pointerPosition.y - this.indicatorEl.getBoundingClientRect().top;
    const xRelativeToParent =
      e.pointerPosition.x - fromLeftOfDraggedElement - this.wrapperDragEl.getBoundingClientRect().left;
    const yRelativeToParent =
      e.pointerPosition.y - fromTopOfDraggedElement - this.wrapperDragEl.getBoundingClientRect().top;
    this.getColorFromCursorPosition({ clientX: xRelativeToParent, clientY: yRelativeToParent }, true);
  }

  public mouseClick(event) {
    this.getColorFromCursorPosition(event, false);
    this.setIndicatorPosition(event.layerX, event.layerY, true);
  }

  public setIndicatorPosition(x, y, flag = false) {
    const tanslate3d = this.getMatrix(this.indicator.nativeElement);
    if (flag && !tanslate3d) {
      this.indicator.nativeElement.style.left = x + 'px';
      this.indicator.nativeElement.style.top = y + 'px';
    } else {
      this.indicator.nativeElement.style.left = x - tanslate3d.x + 'px';
      this.indicator.nativeElement.style.top = y - tanslate3d.y + 'px';
    }
  }

  public getMatrix(element) {
    if (!element.style.transform) return;
    const values = element.style.transform.split(/\w+\(|\);?/);
    const transform = values[1].split(', ').map((item) => parseInt(item));
    return {
      x: transform[0],
      y: transform[1],
      z: transform[2],
    };
  }

  public getColorFromCursorPosition(event, flag) {
    if (!this.ctx) {
      return;
    }
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    let x = !flag ? event.clientX - rect.x : event.clientX;
    if (x < 0) {
      x = 0;
    }
    let y = !flag ? event.clientY - rect.y : event.clientY;
    if (y < 0) {
      y = 0;
    }

    const imageData = this.ctx.getImageData(x, y, 1, 1).data;
    const chosenColor = new RgbColor(imageData[0], imageData[1], imageData[2]);
    if (x === 0 && y === 0) {
      this.changeSubject.next({ r: 255, g: 255, b: 255 });
      return;
    } else if (y >= 213) {
      this.changeSubject.next({ r: 0, g: 0, b: 0 });
      return;
    } else {
      this.changeSubject.next(chosenColor);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.changeSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  ngOnInit(): void {
    this.changeSubscription = this.changeSubject
      .pipe(debounceTime(10), distinctUntilChanged())
      .subscribe((rgbColor: RgbColor) => {
        this._rgbColor = rgbColor;
        this.colorUpdated.emit(rgbColor);
      });
  }

  ngAfterViewInit() {
    if (this.indicator) {
      this.indicatorEl = this.indicator.nativeElement;
    }
    if (this.wrapperDrag) {
      this.wrapperDragEl = this.wrapperDrag.nativeElement;
    }
    this.ctx = this.canvas.nativeElement.getContext('2d');
    this.drawArea();
  }
}
